import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { AnimatePresence, motion } from 'framer-motion'
import { X } from 'lucide-react'
import type { ReactNode } from 'react'

interface BulkActionBarProps {
  count: number
  onClear: () => void
  actions: ReactNode
  noun?: string
  pluralNoun?: string
  className?: string
}

export function BulkActionBar({
  count,
  onClear,
  actions,
  noun = 'item',
  pluralNoun,
  className
}: BulkActionBarProps) {
  const visible = count > 0
  const word = count === 1 ? noun : (pluralNoun ?? `${noun}s`)
  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="bulk-bar"
          initial={{ y: 60, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 60, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 260, damping: 22 }}
          className={cn(
            'fixed inset-x-0 bottom-6 z-30 mx-auto flex w-fit min-w-[320px] max-w-[92vw] items-center gap-3 rounded-full border bg-popover px-4 py-2 text-popover-foreground shadow-lg',
            className
          )}
          role="region"
          aria-label="Bulk actions"
        >
          <span className="text-sm">
            <span className="font-medium tabular-nums">{count}</span>{' '}
            <span className="text-muted-foreground">{word} selected</span>
          </span>
          <span className="h-4 w-px bg-border" />
          <div className="flex items-center gap-1">{actions}</div>
          <span className="h-4 w-px bg-border" />
          <Button
            size="sm"
            variant="ghost"
            onClick={onClear}
            className="size-8 p-0"
          >
            <X className="size-4" />
            <span className="sr-only">Clear selection</span>
          </Button>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
